import { useState } from "react";
import { Button } from "@/components/wensity/button";
import { Badge } from "@/components/wensity/badge";
import { Input } from "@/components/wensity/input";
import { Textarea } from "@/components/wensity/text-area";
import { Select } from "@/components/wensity/select";
import {
  Dialog,
  DialogConfirmation,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogScrollBody,
  DialogTitle,
} from "@/components/wensity/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/wensity/table";
import { contentPath, formatDate, type ContentItem, type ContentKind } from "@/data/site";

const KIND_OPTIONS = [
  { value: "journal", label: "Journal" },
  { value: "guide", label: "Guide" },
  { value: "script", label: "Script" },
];

function emptyDraft(): ContentItem {
  return {
    slug: "",
    kind: "journal",
    title: "",
    description: "",
    date: new Date().toISOString().slice(0, 10),
    tags: [],
    body: "",
  };
}

interface ContentEditorProps {
  items: ContentItem[];
  onChange: (items: ContentItem[]) => void;
}

export function ContentEditor({ items, onChange }: ContentEditorProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<ContentItem>(emptyDraft());
  const [tagsInput, setTagsInput] = useState("");
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContentItem | null>(null);

  const sorted = [...items].sort((a, b) => b.date.localeCompare(a.date));

  function openNew() {
    setDraft(emptyDraft());
    setTagsInput("");
    setEditingSlug(null);
    setDialogOpen(true);
  }

  function openEdit(item: ContentItem) {
    setDraft(item);
    setTagsInput(item.tags.join(", "));
    setEditingSlug(item.slug);
    setDialogOpen(true);
  }

  function handleSave() {
    const tags = tagsInput
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    const saved: ContentItem = { ...draft, tags };

    if (editingSlug) {
      onChange(items.map((item) => (item.slug === editingSlug ? saved : item)));
    } else {
      onChange([...items, saved]);
    }
    setDialogOpen(false);
  }

  function handleDelete(item: ContentItem) {
    onChange(items.filter((existing) => existing.slug !== item.slug));
    setDeleteTarget(null);
  }

  const isValid = draft.slug.trim().length > 0 && draft.title.trim().length > 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="brand" onClick={openNew}>
          Add entry
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((item) => (
            <TableRow key={item.slug}>
              <TableCell>
                <div className="font-medium text-[var(--color-foreground)]">{item.title}</div>
                <div className="text-xs text-[var(--color-muted)]">{contentPath(item)}</div>
              </TableCell>
              <TableCell>
                <Badge variant="neutral" size="sm">
                  {item.kind}
                </Badge>
              </TableCell>
              <TableCell>{formatDate(item.date)}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(item)}>
                    Edit
                  </Button>
                  <DialogConfirmation
                    open={deleteTarget?.slug === item.slug}
                    onOpenChange={(open) => setDeleteTarget(open ? item : null)}
                    trigger={
                      <Button variant="destructive" size="sm">
                        Delete
                      </Button>
                    }
                    title={`Delete "${item.title}"?`}
                    description="This removes it once you save changes. It can't be undone here."
                    confirmLabel="Delete"
                    destructive
                    onConfirm={() => handleDelete(item)}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-[var(--color-muted)]">
                No entries yet.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent variant="scrollable">
          <DialogHeader>
            <DialogTitle>{editingSlug ? "Edit entry" : "New entry"}</DialogTitle>
          </DialogHeader>
          <DialogScrollBody className="space-y-4">
            <Select
              label="Kind"
              options={KIND_OPTIONS}
              value={draft.kind}
              onValueChange={(value) => setDraft((d) => ({ ...d, kind: value as ContentKind }))}
            />
            <Input
              label="Slug"
              hint="Used in the URL, e.g. my-post-title"
              value={draft.slug}
              onChange={(event) => setDraft((d) => ({ ...d, slug: event.target.value }))}
            />
            <Input
              label="Title"
              value={draft.title}
              onChange={(event) => setDraft((d) => ({ ...d, title: event.target.value }))}
            />
            <Textarea
              label="Description"
              value={draft.description}
              onChange={(event) => setDraft((d) => ({ ...d, description: event.target.value }))}
            />
            <Input
              label="Date"
              type="date"
              value={draft.date}
              onChange={(event) => setDraft((d) => ({ ...d, date: event.target.value }))}
            />
            <Input
              label="Tags"
              hint="Comma-separated"
              value={tagsInput}
              onChange={(event) => setTagsInput(event.target.value)}
            />
            <Textarea
              label="Body"
              hint="## headings, **bold**, 1. numbered lists, and ```fenced code blocks``` are supported."
              textareaSize="lg"
              value={draft.body}
              onChange={(event) => setDraft((d) => ({ ...d, body: event.target.value }))}
            />
          </DialogScrollBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="brand" onClick={handleSave} disabled={!isValid}>
              {editingSlug ? "Save changes" : "Add entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
